import { createSlice } from '@reduxjs/toolkit'

import type {
  ProductSource,
  ProductSourceFeed,
} from '@Features/data-management/types/product'

import { useAppDispatch, useAppSelector } from '../hooks'

const INITIAL_DATA_MANAGEMENT_STATE = {
  selectedProductManagement: null as ProductSource | null,
  // Manual/Shopify flows store UI `ProductSource` objects (string `id`);
  // the feed flow stores a `ProductSourceFeed` (numeric `id`).
  selectedProductSource: null as ProductSource | ProductSourceFeed | null,
  disabledProductManagements: [] as string[],
  isProductManagementInitialized: false,
  isProductManagementAlreadySet: false,
}

const dataManagementSlice = createSlice({
  name: 'dataManagement',
  initialState: INITIAL_DATA_MANAGEMENT_STATE,
  reducers: {
    setSelectedProductManagement(state, action) {
      state.selectedProductManagement = action.payload
      state.selectedProductSource = null
      state.isProductManagementInitialized = true
      // Clearing the source must also clear the "already set" flag, otherwise
      // ProductSourceFeed takes the update path and reads a null source's id
      // (→ /conveyor/target/undefined).
      state.isProductManagementAlreadySet = false
    },
    setSelectedProductSource(state, action) {
      state.selectedProductSource = action.payload
    },
    // Refreshes the persisted feed data without touching the user's in-session
    // selection. `initializeFeedManagement` cannot be reused for this: it also
    // rewrites selectedProductManagement and disabledProductManagements, which
    // is exactly what the middleware's already-initialized guard protects.
    refreshFeedSource(state, action) {
      state.selectedProductSource = action.payload
    },
    setDisabledProductManagements(state, action) {
      state.disabledProductManagements = action.payload
      state.isProductManagementInitialized = action.payload.length > 0
    },
    initializeFeedManagement(state, action) {
      state.selectedProductManagement = action.payload.management
      state.selectedProductSource = action.payload.source
      state.isProductManagementInitialized = true
      state.isProductManagementAlreadySet = true
      // While Feed Integration is active, manual imports and Shopify are
      // disabled to prevent mixed product pools (PRD §10/§12). Switching
      // source is CS-controlled.
      state.disabledProductManagements = ['manual', 'shopify']
    },
    initializeShopifyManagement(state, action) {
      state.selectedProductManagement = action.payload.management
      state.selectedProductSource = null
      state.isProductManagementInitialized = true
      state.isProductManagementAlreadySet = true
    },
    resetDataManagementState() {
      return INITIAL_DATA_MANAGEMENT_STATE
    },
  },
})

const { actions, reducer } = dataManagementSlice

export default reducer

export const {
  setSelectedProductManagement,
  setSelectedProductSource,
  setDisabledProductManagements,
  resetDataManagementState,
  initializeFeedManagement,
  initializeShopifyManagement,
  refreshFeedSource,
} = actions

export const useDataManagementState =
  (): typeof INITIAL_DATA_MANAGEMENT_STATE =>
    useAppSelector((state) => state.dataManagement)

export const useDataManagementActions = () => {
  const dispatch = useAppDispatch()

  return {
    setSelectedProductManagement: (payload: any) =>
      dispatch(setSelectedProductManagement(payload)),
    setSelectedProductSource: (payload: any) =>
      dispatch(setSelectedProductSource(payload)),
    setDisabledProductManagements: (payload: any) =>
      dispatch(setDisabledProductManagements(payload)),
    resetDataManagementState: () => dispatch(resetDataManagementState()),
    initializeFeedManagement: (payload: any) =>
      dispatch(initializeFeedManagement(payload)),
    initializeShopifyManagement: (payload: any) =>
      dispatch(initializeShopifyManagement(payload)),
    refreshFeedSource: (payload: any) => dispatch(refreshFeedSource(payload)),
  }
}
